"use client";
import React, { useEffect, useMemo, useRef } from "react";
import Image, { StaticImageData } from "next/image";
import clsx from "clsx";
// PERF: GSAP + ScrollTrigger loaded lazily via ensureGsap() (out of the initial
// route bundle; registerPlugin after mount). Behavior unchanged.
import { ensureGsap } from "@/lib/EnsureGsap";

import { Images } from "../assets/assets";

type StepItem = {
  step: number;
  title: string;
  description: string;
  points: string[];
  cta: string;
  image: StaticImageData | string;
};

export default function DevelopmentProcess() {
  // Lazily load GSAP after mount, then ensure triggers recalc with current layout.
  useEffect(() => {
    let cancelled = false;
    ensureGsap().then(({ ScrollTrigger }) => {
      if (!cancelled) ScrollTrigger.refresh();
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const steps: StepItem[] = useMemo(
    () => [
      {
        step: 1,
        title: "Strategy Development",
        description:
          "The very initial step in our development process is to do thorough market and competitor research to craft a development strategy. You may call it the discovery phase, where we test the feasibility of your idea, its goals, capabilities, and pick the preferred business model.",
        points: [
          "Idea feasibility analysis",
          "Business goals alignment",
          "Market opportunity research",
          "Technology stack selection",
        ],
        cta: "Scalable Strategy Roadmap",
        image: Images?.ourProcess.Strategy_Development,
      },
      {
        step: 2,
        title: "Market Analysis and Planning",
        description:
          "At this stage, we start analyzing your market competitors and target audiences to get a better and clear know-how of the market. By understanding the needs and requirements of your users, we build a clear roadmap about how we’ll reach the end goals.",
        points: [
          "Target audience research",
          "Competitor landscape analysis",
          "User needs evaluation",
          "Market trend insights",
        ],
        cta: "Feature Prioritization Roadmap",
        image: Images?.ourProcess.Analysis_and_Planning,
      },
      {
        step: 3,
        title: "Wireframe and UI/UX Design",
        description:
          "Once everything is set in place, we start <a class='interlink' href='https://www.konstantinfo.com/mvp-development-services'>crafting MVPs</a> and wireframes to visualize how the users will navigate through the final product. Leveraging modern designing tools and frameworks such as Adobe XD, Figma, Sketch, and more to ensure the final product offers a seamless user experience.",
        points: [
          "User journey mapping",
          "Interactive wireframes creation",
          "MVP design concepts",
          "Intuitive interface layouts",
        ],
        cta: "Responsive Design Planning",
        image: Images?.ourProcess.Wireframe_and_UIUX_Design,
      },
      {
        step: 4,
        title: "Final Development",
        description:
          "Now we start transforming prototypes and wireframes and <a class='interlink' href='https://www.konstantinfo.com/'>full-fledged digital solutions</a>. Using latest development technologies and agile methodology, we focus on integrating top-notch features and functionalities, while accelerating development process and ensuring faster time-to-market.",
        points: [
          "Agile development process",
          "Modern technology implementation",
          "Feature-rich development",
          "Secure coding practices",
        ],
        cta: "Performance Optimization Focus",
        image: Images?.ourProcess.Final_Development,
      },
      {
        step: 5,
        title: "Testing and Quality Assurance",
        description:
          "Once the app is developed, we move on to the testing phase. Our team of QA and testers perform rigorous automated and manual tests including functional testing, performance testing, user acceptance testing, and usability testing to resolve each and every issue and error. ",
        points: [
          "Functional testing checks",
          "Automated test execution",
          "Manual quality validation",
          "Performance benchmarking",
        ],
        cta: "Bug-Free Delivery Assurance",
        image: Images?.ourProcess.Testing_and_Quality_Assurance_process,
      },

      {
        step: 6,
        title: "Deployment and Support",
        description:
          "After we ensure that the app is free from all the glitches and bugs, we deploy it to the Play Store or Apple’s App Store. Our developers ensure the app adheres to Google and Apple Store guidelines. Moreover, our support and maintenance services ensure your app stays up-to-date and delivers seamless performance at all times. ",
        points: [
          "App store deployment",
          "Compliance guideline checks",
          "Post-launch monitoring",
          "Ongoing maintenance support",
        ],
        cta: "Regular Updates Delivery",
        image: Images?.ourProcess.Deployment_and_Support,
      },
    ],
    [],
  );

  const stepsRootRef = useRef<HTMLDivElement | null>(null);

  // Master timeline to animate all lines/dots sequentially with one trigger (desktop only)
  useEffect(() => {
    if (!stepsRootRef.current) return;

    // Only run animations on desktop (md breakpoint and above)
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    if (!mediaQuery.matches) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    ensureGsap().then(({ gsap }) => {
      if (cancelled) return;

      const ctx = gsap.context(() => {
        const progressEls = gsap.utils.toArray<HTMLElement>(".dp-progress");
        const dotEls = gsap.utils.toArray<HTMLElement>(".dp-dot");

        // initial states
        gsap.set(progressEls, { scaleY: 0, transformOrigin: "top" });
        gsap.set(dotEls, {
          backgroundColor: "#ffffff",
          borderColor: "#ECF0F6",
          boxShadow: "0 0 0 rgba(0,0,0,0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: stepsRootRef.current,
            start: "top 85%",
            end: "bottom 10%",
            scrub: 1,
            invalidateOnRefresh: true,
          },
        });

        progressEls.forEach((el, i) => {
          tl.to(el, { scaleY: 1, ease: "none", duration: 1 }).to(
            dotEls[i],
            {
              backgroundColor: "#225BD4",
              borderColor: "#d9e2ed",
              boxShadow: "0 4px 34px rgba(34,91,212,0.4)",
              duration: 0.2,
            },
            "<",
          );
        });
      }, stepsRootRef);

      cleanup = () => ctx.revert();
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  return (
    <section className="py-[60px] md:py-[100px] lg:py-[150px] bg-[#fff]">
      <div className="press-header max-w-[2500px] mx-auto w-[calc(100%_-_10%)] border-b border-white pb-[20px] justify-between flex flex-col lg:flex-row items-start lg:items-center gap-[15px]">
        <div className="press-header-left lg:w-auto leading-[110%]">
          <h5 className="self-stretch justify-center text-[#636363] text-sm md:text-base font-medium uppercase tracking-tight pb-4">
            Development Process
          </h5>
          <h2 className="self-stretch lg:flex flex-col justify-start text-[length:var(--section-title)] font-medium clash-font">
            <span className="text-[#00040b] capitalize leading-[110%]">
              Our Development Process <br className="hidden lg:inline"></br> to
              Build Solutions That{" "}
            </span>
            <span className="text-[#225bd4] capitalize leading-[110%]">
              {" "}
              Actually Work
            </span>
          </h2>
        </div>
        <div className="press-header-right flex-1 text-[length:var(--hero-para)] max-w-full lg:max-w-[50ch] mt-10">
          <div className="w-full lg:max-w-[60ch] justify-start text-[#00040b]/70 text-[length:var(--hero-para)] font-normal leading-[150%]">
            No matter if you want to develop a web app, custom software,
            enterprise-grade solution, or a web app, we follow a carefully
            strategized development approach to deliver user-friendly, secure,
            and high-performing solutions. Following a structured development
            approach, our developers ensure the final solution aligns with your
            goals and offers cost control, long-term scalability, and scales
            with your growing business needs.
          </div>
        </div>
      </div>

      {/* Steps */}
      <div
        ref={stepsRootRef}
        className="max-w-[1600px] mx-auto w-[calc(100%_-_10%)] mt-8 md:mt-12 lg:mt-16 space-y-8 md:space-y-6 lg:space-y-6"
      >
        {steps?.map((s, idx) => (
          <StepWithDot
            key={s.step}
            item={s}
            reverse={idx % 2 === 1}
            isFirst={idx === 0}
          />
        ))}
      </div>
    </section>
  );
}

function StepWithDot({
  item,
  reverse,
  isFirst,
}: {
  item: StepItem;
  reverse?: boolean;
  isFirst?: boolean;
}) {
  return (
    <>
      {/* Circle row centered (becomes active when step enters) - Hidden on mobile */}
      <div className="relative h-5 hidden md:block">
        <span className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-full w-px bg-[#ECF0F6]" />
        <span
          className={clsx(
            "dp-dot absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[22px] h-[22px] rounded-full border border-[#ECF0F6] bg-white",
            isFirst ? "is-active" : "",
          )}
        />
      </div>

      {/* Step row */}
      <div className="relative">
        {/* Line track and progress (per-step) - Hidden on mobile */}
        <span className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-full w-px bg-[#ECF0F6] hidden md:block" />
        <span className="dp-progress pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 h-full w-px bg-[#225BD4] hidden md:block" />

        {/* Mobile: Image first, then text. Desktop: alternating layout */}
        <div
          className={clsx(
            "relative flex flex-col items-center gap-6 md:gap-10 lg:gap-30",
            "md:flex-row",
            reverse ? "md:flex-row-reverse" : "",
          )}
        >
          {/* Image - Shows first on mobile */}
          <div className="w-full md:w-1/2 order-1 md:order-none">
            <Image
              src={item.image}
              alt={item.title}
              className="w-full max-w-[520px] mx-auto"
              width={550}
              height={550}
              priority={item.step === 1}
              fetchPriority="high"
            />
          </div>

          {/* Text - Shows second on mobile */}
          <div className="w-full md:w-1/2 order-2 md:order-none">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#ECF0F6] px-5 py-1 text-[#225BD4] text-sm font-semibold uppercase">
              {`Step ${item.step}`}
            </div>
            <h3 className="pt-4 text-[28px] md:text-[38px] font-[500] leading-[120%] clash-font text-[#00040b]">
              {item.title}
            </h3>
            <p
              className="mt-4 text-[#636364] text-base leading-[140%]"
              dangerouslySetInnerHTML={{ __html: item.description ?? "" }}
            />
            <ul className="mt-6 space-y-3 pl-[19px] list-disc">
              {item.points?.map((p) => (
                <li
                  key={p}
                  className="text-[#00040b] text-[16px] md:text-[18px] leading-[140%] font-medium italic"
                >
                  {p}
                </li>
              ))}
            </ul>
            <div className="pt-6">
              <button
                aria-label={item.cta}
                className="w-full h-[42px] bg-[#225BD4] text-white text-[16px] md:text-[18px] font-bold grid place-items-center rounded-none hover:bg-[#1b4ab0] transition-colors"
              >
                {item.cta}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
